import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiEye, FiEdit3, FiTrash2 } from "react-icons/fi";
import { Link } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import { DndContext, closestCenter } from "@dnd-kit/core";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";

const CategoryTable = () => {
  const [rows, setRows] = useState([]);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
    const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;

const fetchData = async () => {
  try {
    const [catRes, featureRes] = await Promise.all([
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/stock-categories`),
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/features`).catch(() => ({ data: [] })), 
    ]);

    const list = catRes.data;
    const features = featureRes.data;

    const sortedList = list.sort((a, b) => a.sort_order - b.sort_order);

    const mapped = sortedList.map((c) => ({
      ...c,
      parent_title: list.find((p) => p.id === c.parent_id)?.title || "-",
      isUsedAsParent: list.some((child) => child.parent_id === c.id),
      hasFeatures: features.some((f) => (f.categories || []).includes(c.id)),
    }));

    setRows(mapped);
  } catch (error) {
    console.error("Veri çekme hatası", error);
  }
};

  useEffect(() => {
    fetchData();
  }, []);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const overIndex = rows.findIndex((r) => r.id === over.id);

    // UI için arrayMove'a gönderilecek index
    let targetIndex = overIndex;

    // Backend'e gönderilecek index (backend +1 yaptığı için -1 gönderiyoruz)
    let backendIndex = overIndex - 1;

    // Aşağı taşırken ek düzeltme
    if (oldIndex < overIndex) {
      // Aşağı taşırken
      targetIndex = overIndex - 1;
    }

    // UI için anında güncelle
    const newRows = arrayMove(rows, oldIndex, targetIndex);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/stock-categories/sort`,
        {
          movedId: active.id,
          newIndex: backendIndex, // Backend +1 yapacak
        }
      );

      toast.success("Sıralama güncellendi");
      fetchData();
    } catch (error) {
      toast.error("Sıralama kaydedilemedi");
      fetchData();
    }
  };
  const TinySwitch = ({ checked, onChange }) => (
    <div className="form-check form-switch text-center">
      <input
        className="form-check-input"
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{
          width: "45px",
          height: "22px",
          cursor: "pointer",
        }}
      />
    </div>
  );
const toggleHomepage = async (id) => {
  try {
    await axios.put(
      `${API_BASE}/api/stock-categories/homepage/${id}`
    );

    toast.success("Anasayfa durumu güncellendi");
    fetchData();
  } catch (err) {
    console.error(err);
    toast.error("Anasayfa durumu güncellenemedi");
  }
};


  //  SWITCH - Durum değiştirme
  const StatusSwitch = ({ id, current }) => {
    const [checked, setChecked] = useState(current === "aktif");

    const toggle = async () => {
      const newStatus = checked ? "pasif" : "aktif";
      setChecked(!checked);

      try {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/stock-categories/${id}`,
          { status: newStatus }
        );
        toast.success("Durum güncellendi");
        fetchData();
      } catch (err) {
        toast.error("Durum güncellenemedi");
        setChecked(checked);
      }
    };

    return (
      <div className="form-check form-switch text-center">
        <input
          className="form-check-input"
          type="checkbox"
          checked={checked}
          onChange={toggle}
          style={{ cursor: "pointer", width: 45, height: 22 }}
        />
      </div>
    );
  };

  const handleDelete = (id) => {
    confirmAlert({
      title: getText("DESTROY_CONFIRM_TITLE"),
      message: getText("DESTROY_CONFIRM_MSG"),
      buttons: [
        {
          label: "Evet, Sil",
          onClick: async () => {
            try {
              await axios.delete(
                `${process.env.REACT_APP_API_BASE_URL}/api/stock-categories/${id}`
              );
               toast.success(getText("SUCCESS_USER_SOFT_DELETED"));
              fetchData();
            } catch {
              toast.error(getText("ERROR_USER_SOFT_DELETE_FAILED"));
            }
          },
        },
        { label: getText("CANCEL")},
      ],
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "sort",
        header: getText("TABLE_SORT_LABEL"),
        size: 40,
        // Dnd-Kit handle'ı için class veya style
        cell: () => (
          <span style={{ cursor: "grab", fontSize: "1.2rem" }}>☰</span>
        ),
      },
      { accessorKey: "id", header: "ID", size: 70 },
      {
        accessorKey: "image",
        header: getText("CATEGORY_IMAGE_ALT"),
        cell: (info) => {
          const src = info.getValue();
          if (!src) return null;
          return (
            <img
              src={`${process.env.REACT_APP_API_BASE_URL}${src}`}
              style={{ width: 60, height: 60, borderRadius: 6 }}
            />
          );
        },
      },
      { accessorKey: "title",  header: getText("TABLE_TITLE_LABEL") },
      {
        accessorKey: "parent_title",
       header: getText("TABLE_TOP_CATEGORY_LABEL"),
        cell: (info) => info.getValue() || "-",
      },
      {
        accessorKey: "show_homepage",
        header: getText("TABLE_HOMEPAGE_LABEL"),
        size: 80,
        cell: ({ row }) => (
          <TinySwitch
            checked={Boolean(row.original.show_homepage)}
            onChange={() => toggleHomepage(row.original.id)}
          />
        ),
      },

      //  Status Switch Column
      {
        accessorKey: "status",
        header: getText("TABLE_STATUS_LABEL"),
        cell: (info) => (
          <StatusSwitch id={info.row.original.id} current={info.getValue()} />
        ),
        size: 150,
      },

      {
        accessorKey: "created_at",
        header: getText("TABLE_DATE_LABEL"),
        cell: (info) => new Date(info.getValue()).toLocaleDateString("tr-TR"),
      },
      {
        accessorKey: "actions",
        header: getText("TABLE_ACTIONS_LABEL"),
        cell: (info) => {
          const row = info.row.original;
          const id = row.id;

          const isDisabled = row.isUsedAsParent || row.hasFeatures;

          return (
            <div className="d-flex gap-2">
              <Link
                to={`/categories/view/${id}`}
                className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
                title={getText("VIEW")}
              >
                <FiEye />
              </Link>

              <Link
                to={`/categories/edit/${id}`}
                className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
                title={getText("EDIT")}
              >
                <FiEdit3 />
              </Link>

              {/* Sil Butonu */}
              <button
                disabled={isDisabled}
                title={
                  isDisabled
                    ? getText("DELETE_CANNOT")
                    : getText("DELETE")
                }
                onClick={() => !isDisabled && handleDelete(id)}
                className={`avatar-text avatar-md ${
                  isDisabled
                    ? "text-secondary bg-light"
                    : "text-danger bg-soft-danger border-soft-danger"
                }`}
                style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
              >
                <FiTrash2 />
              </button>
            </div>
          );
        },
      },
    ],
    [translations]
  );

  return (
    <Table
      data={rows}
      columns={columns}
      enableRowSort={true}
      rowIdKey="id"
      onRowSortEnd={handleDragEnd}
    />
  );
};

export default CategoryTable;
